import decorateComponentWithProps from 'decorate-component-with-props';

import {
  BUTTON_BOLD,
  BUTTON_ITALIC,
  BUTTON_TITLE,
  BUTTON_SUBTITLE,
  BUTTON_NUMBERING,
  BUTTON_BULLETS,
  BUTTON_BLOCKQUOTE,
  BUTTON_CODE_BLOCK,
  BUTTON_LINK,
  BUTTON_HEADER_DROPDOWN,
  BUTTON_LIST_DROPDOWN,
  BUTTON_ALIGN_TEXT_DROPDOWN,
  BUTTON_IMAGE,
  BUTTON_IMAGE_MEDIA_DIALOG,
  BUTTON_VIDEO,
  BUTTON_ALIGN_LEFT,
  BUTTON_ALIGN_RIGHT,
  BUTTON_ALIGN_CENTER,
  BUTTON_ALIGN_JUSTIFY,
} from './constants/buttons';
import AlignLeftIcon from './icons/align-left-icon';
import AlignRightIcon from './icons/align-right-icon';
import AlignTextCenterIcon from './icons/align-text-center-icon';
import AlignTextJustifyIcon from './icons/align-text-justify-icon';
import AlignTextLeftIcon from './icons/align-text-left-icon';
import AlignTextRightIcon from './icons/align-text-right-icon';
import BoldIcon from './icons/bold-icon';
import CameraIcon from './icons/camera-icon';
import DeleteIcon from './icons/delete-icon';
import FitToPageIcon from './icons/fit-to-page-icon';
import FullWidthIcon from './icons/full-width-icon';
import HeaderIcon from './icons/header-icon';
import HeaderSmallIcon from './icons/header-small-icon';
import ItalicIcon from './icons/italic-icon';
import LinkIcon from './icons/link-icon';
import OrderedListIcon from './icons/ordered-list-icon';
import OriginalSizeIcon from './icons/original-size-icon';
import QuoteIcon from './icons/quote-icon';
import CodeIcon from './icons/code-icon';
import ReplaceIcon from './icons/replace-icon';
import SmallCenterIcon from './icons/small-center-icon';
import UnorderedListIcon from './icons/unordered-list-icon';
import VideoIcon from './icons/video-icon';
import createAlignmentButton from './utils/create-alignment-button';
import createBlockTypeButton from './utils/create-block-type-button';
import createButton from './utils/create-button';
import createDropdownButton from './utils/create-dropdown-button';
import createInlineStyleButton from './utils/create-inline-style-button';
import createSizeButton from './utils/create-size-button';
import createUploadButton from './utils/create-upload-button';
import createMediaDialogButton from './utils/create-media-dialog-button';

export const BoldButton = createInlineStyleButton({
  inlineStyle: 'BOLD',
  content: decorateComponentWithProps(BoldIcon, {
    width: 12,
  }),
  tooltipText: 'text-editor.tooltip-bold',
});

export const ItalicButton = createInlineStyleButton({
  inlineStyle: 'ITALIC',
  content: decorateComponentWithProps(ItalicIcon, {
    width: 10,
  }),
  tooltipText: 'text-editor.tooltip-italic',
});

export const HeaderDropdownButton = createDropdownButton({
  className: 'text-editor__header-dropdown-button',
  buttons: [
    {
      value: BUTTON_TITLE,
      icon: decorateComponentWithProps(HeaderIcon, {
        width: 11,
      }),
      tooltipText: 'text-editor.tooltip-header-one',
      className: 'text-editor__header-one-button',
    },
    {
      value: BUTTON_SUBTITLE,
      icon: decorateComponentWithProps(HeaderSmallIcon, {
        width: 10,
        style: {
          paddingTop: 2,
        },
      }),
      tooltipText: 'text-editor.tooltip-header-two',
      className: 'text-editor__header-two-button',
    },
  ],
});

export const HeaderOneButton = createBlockTypeButton({
  blockType: 'header-one',
  content: decorateComponentWithProps(HeaderIcon, {
    width: 11,
  }),
  tooltipText: 'text-editor.tooltip-header-one',
});

export const HeaderTwoButton = createBlockTypeButton({
  blockType: 'header-two',
  content: decorateComponentWithProps(HeaderSmallIcon, {
    width: 10,
    style: {
      paddingTop: 2,
    },
  }),
  tooltipText: 'text-editor.tooltip-header-two',
});

export const ListDropdownButton = createDropdownButton({
  className: 'text-editor__list-dropdown-button',
  buttons: [
    {
      value: BUTTON_NUMBERING,
      icon: decorateComponentWithProps(OrderedListIcon, {
        width: 16,
      }),
      className: 'text-editor__ordered-list-item-button',
    },
    {
      value: BUTTON_BULLETS,
      icon: decorateComponentWithProps(UnorderedListIcon, {
        width: 16,
      }),
      className: 'text-editor__unordered-list-item-button',
    },
  ],
});

export const OrderedListButton = createBlockTypeButton({
  blockType: 'ordered-list-item',
  content: decorateComponentWithProps(OrderedListIcon, {
    width: 16,
  }),
  tooltipText: 'text-editor.tooltip-ordered-list-item',
});

export const UnorderedListButton = createBlockTypeButton({
  blockType: 'unordered-list-item',
  content: decorateComponentWithProps(UnorderedListIcon, {
    width: 16,
  }),
  tooltipText: 'text-editor.tooltip-unordered-list-item',
});

export const QuoteButton = createBlockTypeButton({
  blockType: 'blockquote',
  content: decorateComponentWithProps(QuoteIcon, {
    width: 15,
  }),
  tooltipText: 'text-editor.tooltip-blockquote',
});

export const CodeBlockButton = createBlockTypeButton({
  blockType: 'code-block',
  content: decorateComponentWithProps(CodeIcon, {
    width: 17,
  }),
  tooltipText: 'text-editor.tooltip-code-block',
});

export const ImageButton = createUploadButton({
  className: 'text-editor__upload-image-button',
  content: decorateComponentWithProps(CameraIcon, {
    width: 20,
  }),
  tooltipText: 'text-editor.tooltip-image-upload',
});

export const ImageMediaDialogButton = createMediaDialogButton({
  className: 'text-editor__upload-image-button',
  content: decorateComponentWithProps(CameraIcon, {
    width: 20,
  }),
  tooltipText: 'text-editor.tooltip-image-upload',
});

export const VideoButton = createButton({
  className: 'text-editor__add-video-button',
  content: decorateComponentWithProps(VideoIcon, {
    width: 20,
  }),
  tooltipText: 'text-editor.tooltip-video',
});

export const AlignTextDropdownButton = createDropdownButton({
  className: 'text-editor__text-alignment-dropdown-button',
  buttons: [
    {
      value: BUTTON_ALIGN_LEFT,
      icon: decorateComponentWithProps(AlignTextLeftIcon, {
        width: 16,
      }),
      tooltipText: 'text-editor.tooltip-align-text-left',
      className: 'text-editor__text-alignment-left-button',
    },
    {
      value: BUTTON_ALIGN_RIGHT,
      icon: decorateComponentWithProps(AlignTextRightIcon, {
        width: 16,
      }),
      tooltipText: 'text-editor.tooltip-align-text-right',
      className: 'text-editor__text-alignment-right-button',
    },
    {
      value: BUTTON_ALIGN_CENTER,
      icon: decorateComponentWithProps(AlignTextCenterIcon, {
        width: 16,
      }),
      tooltipText: 'text-editor.tooltip-align-text-center',
      className: 'text-editor__text-alignment-center-button',
    },
    {
      value: BUTTON_ALIGN_JUSTIFY,
      icon: decorateComponentWithProps(AlignTextJustifyIcon, {
        width: 16,
      }),
      tooltipText: 'text-editor.tooltip-align-text-justify',
      className: 'text-editor__text-alignment-justify-button',
    },
  ],
});

export const AlignLeftButton = createAlignmentButton({
  alignment: 'left',
  content: decorateComponentWithProps(AlignLeftIcon, {
    width: 20,
  }),
  tooltipText: 'text-editor.tooltip-align-left',
});

export const AlignRightButton = createAlignmentButton({
  alignment: 'right',
  content: decorateComponentWithProps(AlignRightIcon, {
    width: 20,
  }),
  tooltipText: 'text-editor.tooltip-align-right',
});

export const SizeTextButton = createSizeButton({
  size: 'smallCenter',
  content: decorateComponentWithProps(SmallCenterIcon, {
    width: 14,
  }),
  tooltipText: 'text-editor.tooltip-small-center',
});

export const SizeContentButton = createSizeButton({
  size: 'content',
  content: decorateComponentWithProps(FitToPageIcon, {
    width: 14,
  }),
  tooltipText: 'text-editor.tooltip-fit-to-page',
});

export const SizeFullWidthButton = createSizeButton({
  size: 'fullWidth',
  content: decorateComponentWithProps(FullWidthIcon, {
    width: 20,
  }),
  tooltipText: 'text-editor.tooltip-full-width',
});

export const SizeOriginalButton = createSizeButton({
  size: 'original',
  content: decorateComponentWithProps(OriginalSizeIcon, {
    width: 20,
    style: {
      stroke: 'none',
    },
  }),
  tooltipText: 'text-editor.tooltip-original-size',
});

export const ReuploadButton = createUploadButton({
  className: 'text-editor__reupload-image-button',
  content: decorateComponentWithProps(ReplaceIcon, {
    width: 16,
  }),
  tooltipText: 'text-editor.tooltip-change',
});

export const ReuploadMediaDialogButton = createMediaDialogButton({
  className: 'text-editor__reupload-image-button',
  content: decorateComponentWithProps(ReplaceIcon, {
    width: 16,
  }),
  tooltipText: 'text-editor.tooltip-change',
});

export const ChangeButton = createButton({
  displayName: 'ChangeButton',
  className: 'text-editor__change-video',
  content: decorateComponentWithProps(ReplaceIcon, {
    width: 16,
  }),
  tooltipText: 'text-editor.tooltip-change',
});

export const LinkButton = createButton({
  displayName: 'LinkButton',
  className: 'text-editor__add-link-button',
  content: decorateComponentWithProps(LinkIcon, {
    width: 16,
  }),
  tooltipText: 'text-editor.tooltip-link',
});

export const DeleteButton = createButton({
  displayName: 'DeleteButton',
  className: 'text-editor__delete-button',
  content: decorateComponentWithProps(DeleteIcon, {
    width: 13,
  }),
  tooltipText: 'text-editor.tooltip-delete',
});

export default {
  [BUTTON_BOLD]: BoldButton,
  [BUTTON_ITALIC]: ItalicButton,
  [BUTTON_TITLE]: HeaderOneButton,
  [BUTTON_SUBTITLE]: HeaderTwoButton,
  [BUTTON_HEADER_DROPDOWN]: HeaderDropdownButton,
  [BUTTON_LIST_DROPDOWN]: ListDropdownButton,
  [BUTTON_NUMBERING]: OrderedListButton,
  [BUTTON_BULLETS]: UnorderedListButton,
  [BUTTON_BLOCKQUOTE]: QuoteButton,
  [BUTTON_CODE_BLOCK]: CodeBlockButton,
  [BUTTON_ALIGN_TEXT_DROPDOWN]: AlignTextDropdownButton,
  [BUTTON_LINK]: LinkButton,
  [BUTTON_IMAGE]: ImageButton,
  [BUTTON_IMAGE_MEDIA_DIALOG]: ImageMediaDialogButton,
  [BUTTON_VIDEO]: VideoButton,
};



// WEBPACK FOOTER //
// ./app/components/text-editor/buttons.js